import React from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { CatalogPagination } from '../../../shared/ui/CatalogPagination';
import { MastersEmptyState } from './MastersEmptyState';
import { styles } from './CatalogMastersScreen.styles';

type MastersSectionListShellProps = {
  loading: boolean;
  error: string | null;
  itemCount: number;
  totalCount: number | null;
  pageIndex: number;
  pageSize: 5 | 10;
  hasMore: boolean;
  listNouns: { singular: string; plural: string };
  loadingLabel: string;
  emptyIcon: React.ComponentProps<typeof MastersEmptyState>['icon'];
  emptyTitle: string;
  emptyErrorTitle: string;
  emptyMessage: string;
  canEdit: boolean;
  createLabel: string;
  onCreate: () => void;
  /** Si hay error y llega onRetry, el empty-state muestra «Reintentar». */
  onRetry?: () => void;
  isWide: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onPageSizeChange?: (size: 5 | 10) => void;
  /** Si false, no muestra pie de paginación (p. ej. categorías cargan el árbol completo). */
  showPagination?: boolean;
  renderWide: () => React.ReactNode;
  renderMobile: () => React.ReactNode;
};

export function MastersSectionListShell({
  loading,
  error,
  itemCount,
  totalCount,
  pageIndex,
  pageSize,
  hasMore,
  listNouns,
  loadingLabel,
  emptyIcon,
  emptyTitle,
  emptyErrorTitle,
  emptyMessage,
  canEdit,
  createLabel,
  onCreate,
  onRetry,
  isWide,
  onPreviousPage,
  onNextPage,
  onPageSizeChange,
  showPagination = true,
  renderWide,
  renderMobile,
}: MastersSectionListShellProps) {
  const initialLoading = loading && itemCount === 0;
  const paginationVisible = showPagination && (itemCount > 0 || pageIndex > 0);

  return (
    <>
      {initialLoading ? (
        <View style={[styles.listEmpty, { gap: spacing.sm }]}>
          <ActivityIndicator color={ticketColors.ink2} />
          <Text style={styles.listEmptyText}>{loadingLabel}</Text>
        </View>
      ) : itemCount === 0 ? (
        <MastersEmptyState
          icon={emptyIcon}
          title={error ? emptyErrorTitle : emptyTitle}
          message={error ?? emptyMessage}
          canEdit={canEdit}
          createLabel={createLabel}
          onCreate={onCreate}
          onRetry={error ? onRetry : undefined}
        />
      ) : isWide ? (
        renderWide()
      ) : (
        <View style={{ gap: 0 }}>{renderMobile()}</View>
      )}

      {paginationVisible ? (
        <CatalogPagination
          variant="default"
          placement="footer"
          catalogPageIndex={pageIndex}
          pageSize={pageSize}
          catalogTotalCount={totalCount}
          hasMore={hasMore}
          productsLoading={loading}
          productCount={itemCount}
          listNouns={listNouns}
          alwaysShowPagination
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          pageSizePicker={
            onPageSizeChange
              ? {
                  label: 'Página',
                  options: [5, 10],
                  value: pageSize,
                  onChange: (size: number) => onPageSizeChange(size as 5 | 10),
                }
              : undefined
          }
        />
      ) : null}
    </>
  );
}
